import { initTheme } from './lib/theme'
import { initImgFade } from './lib/imgFade'
// Chrome/Android fires beforeinstallprompt ONCE, early; any listener added after
// the library loads misses it and the one-tap Install never appears (the old
// InstallBanner, 2026-10-15). Capture it here and hand it to InstallAsk.
window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); window.__nextepInstallEvt = e; try { window.dispatchEvent(new Event('nextep:installable')) } catch {} })
initTheme() // restore saved theme before first render
import { installSandboxGuard } from './lib/labs'
installSandboxGuard() // before anything can fetch — see lib/labs.js
initImgFade()
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { APP_BUILD_ID } from './lib/appVersion'
import { initMonitoring, reportError } from './lib/monitoring'
import { logEvent } from './lib/analytics'
import { hardReloadForUpdate, recoverFromStaleChunk, showUpdatingSplash, isUpdatingReload, clearUpdatingSplash, isStandalonePWA, deferReloadToBackground, announceQueuedUpdate, clearQueuedUpdateAnnouncement } from './lib/staleChunk'
import './index.css'

// Shell-level crash fallback. Uses LITERAL colors (not CSS tokens) on purpose: if
// the app crashed hard enough to hit this, theming can't be assumed. The per-route
// ErrorBoundary in App already contains page crashes; this catches provider/shell
// failures that would otherwise be a white screen until the 5s watchdog reloads.
const AppCrashFallback = (
  <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: '#07070f', color: '#eeeeff', fontFamily: '-apple-system, system-ui, sans-serif', padding: 24, textAlign: 'center' }}>
    <div style={{ fontSize: 34 }}>🎬</div>
    <div style={{ fontWeight: 600, fontSize: 17 }}>Something went wrong</div>
    <div style={{ color: '#918eac', fontSize: 13, maxWidth: 300, lineHeight: 1.5 }}>NextEp hit an unexpected error. Reloading almost always fixes it.</div>
    <button onClick={() => window.location.reload()} style={{ marginTop: 6, background: 'var(--purple)', color: '#07070f', border: 0, borderRadius: 12, padding: '11px 26px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Reload</button>
  </div>
)

// Defer Sentry init off the critical boot path. It's a dynamic-imported ~150 kB
// (gzip) chunk for error monitoring, not UX — loading it during idle instead of
// the initial burst keeps it from competing with the app's critical JS on a cold
// mobile load. The ErrorBoundary still catches crashes in the meantime; the
// timeout guarantees monitoring is up within a few seconds regardless.
// (no-op entirely unless VITE_SENTRY_DSN is set — see lib/monitoring.js)
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => initMonitoring(), { timeout: 4000 })
  else setTimeout(() => initMonitoring(), 2500)
  // Behavioral friction telemetry (rage-taps / dead-taps / crashes → ui_friction
  // events + War Room threshold alerts). Tiny and dependency-free, but still not
  // boot-critical, so it rides the same idle slot as Sentry.
  import('./lib/frictionTelemetry').then(m => m.initFrictionTelemetry()).catch(() => {})
}

// ── Auth bridge: magic link → cookie → PWA session ──────────────────────────
// When a magic link is clicked from Mail, it opens in Safari (not the PWA).
// Supabase puts the session tokens in the URL hash. Since iOS Safari and the
// home-screen PWA have ISOLATED localStorage (the root of the login loop), we
// set a short-lived cookie instead — cookies ARE shared across contexts on the
// same domain on iOS 16.4+. When the PWA next opens, it reads the cookie and
// calls setSession() to store the tokens in its own localStorage.
;(function () {
  try {
    const hash = window.location.hash.slice(1)
    if (!hash) return
    const p = new URLSearchParams(hash)
    const at = p.get('access_token'), rt = p.get('refresh_token')
    if (!at || !rt) return
    // Pack tokens into a base64 payload for the cookie
    const payload = btoa(JSON.stringify({ at, rt, ts: Date.now() }))
    // 5-minute cookie — long enough to switch contexts, short enough to be safe
    const exp = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
    document.cookie = `nxab=${payload}; expires=${exp}; path=/; SameSite=Lax; Secure`
    try { sessionStorage.setItem('nxab', payload) } catch {}
    // Clean the token hash so the URL bar looks normal — but PRESERVE the query
    // string. The magic-link redirect can carry ?invite=CODE / ?from=tvtime, and the
    // app reads those after boot (invite attribution, TV Time importer) — replacing
    // with a bare '/' here silently dropped them on the cross-device link path.
    history.replaceState(null, '', window.location.pathname + window.location.search)
  } catch {}
})()

// ── Auto-reload when a new Service Worker takes control ───────────────────────
// Prevents the stuck-on-old-bundle problem: as soon as Workbox's new SW activates
// (clientsClaim takes effect), this listener fires and forces the page to refetch
// the HTML + JS with the new precache. The `refreshing` flag prevents reload loops.
// Shared one-shot guard so the two reload paths below (SW controllerchange and the
// version-mismatch fallback) cannot stack into a double reload. Whichever fires first
// claims the guard; the other stands down. The cooldown expires on its own — there is
// no explicit "release", clearing it early would just re-open the door to a second reload.
const RELOAD_AT = 'nextep_reload_at'
const RELOAD_COOLDOWN = 12000  // ms - suppress repeat reloads within this window so one update cannot trigger a burst
const reloadClaimed = () => { try { return Date.now() - (parseInt(sessionStorage.getItem(RELOAD_AT), 10) || 0) < RELOAD_COOLDOWN } catch { return false } }
const claimReload   = () => { try { sessionStorage.setItem(RELOAD_AT, String(Date.now())) } catch {} }

// An INSTALLED standalone PWA (iOS home-screen especially) must NOT auto-reload for a
// routine update: any in-place JS reload/replace corrupts the layout viewport there —
// the fixed bottom nav drifts to mid-screen and taps miss, curable only by a force-quit —
// and it double-boots (the ~10s "Updating…" open). A real cold start, by contrast, is
// instant and clean and picks up the waiting service worker on its own. So on standalone
// we let updates apply on the NEXT cold start and skip every automatic reload; only
// genuine breakage (a stale lazy chunk 404, a blank #root) still reloads, since there a
// reload beats a crash. Browser tabs keep auto-reloading — no drift there, and they don't
// cold-start on their own (Jason 2026-07-23, after the reload→replace + re-anchor attempts
// still floated the nav; only force-quit fixed it). isStandalonePWA now lives in
// staleChunk.js so every reload path shares one definition (2026-08-22 dead-nav fix).

if ('serviceWorker' in navigator) {
  // Only reload when a NEW sw REPLACES an existing controller (a real update). On the very
  // FIRST install there's no prior controller — clientsClaim fires controllerchange just to
  // take control of this fresh load, and reloading there makes the app visibly "load twice"
  // (it already painted). Skip that first claim (2026-07-21).
  let hadController = !!navigator.serviceWorker.controller
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) { hadController = true; return }   // first-install claim, not an update
    if (isStandalonePWA) return   // standalone: the new SW is live now; let the app pick it up on the next cold start (no drift-inducing reload)
    if (reloadClaimed()) return
    claimReload()
    showUpdatingSplash()   // cover the old layout while the new bundle boots
    // Use location.replace(), NOT location.reload(). On an iOS standalone PWA a JS
    // reload() often leaves position:fixed elements (the bottom nav) drifted to
    // mid-screen with their touch target desynced from where they paint — so the nav
    // "floats" and taps miss ("menus stop working"), only cured by a force-quit. A
    // navigation via replace() re-establishes the fixed containing block cleanly. The
    // other two reload paths (version-mismatch, stale-chunk) already use replace() for
    // this reason; this one had been left on reload() (Jason 2026-07-22).
    // Tiny delay so React can finish its current render before we yank the page.
    setTimeout(() => window.location.replace(window.location.href), 50)
  })

  // ── Announce a GENUINELY-waiting SW so the update prompt is confident + prompt ──
  // The WhatsNew "Update now" prompt had been driven only by polling version.json,
  // which flips the instant a deploy lands — BEFORE the new SW has finished downloading
  // its precache. Tapping then raced a not-yet-ready worker and often reloaded onto the
  // SAME old bundle (the "half-works, tap again" bug). Here we watch the ACTUAL SW
  // lifecycle and fire `nextep:update-waiting` the moment a new worker reaches
  // "installed" while we already have a controller (= truly waiting). We NEVER
  // auto-skipWaiting here — activation still waits for the user's tap, so the standalone
  // in-place-reload drift guard above is unchanged. This just makes the prompt appear as
  // soon as the worker is ready (not on the next 90s poll) and only when a tap will work.
  const announceWaiting = () => { try { window.dispatchEvent(new CustomEvent('nextep:update-waiting')) } catch {} }
  navigator.serviceWorker.getRegistration().then(reg => {
    if (!reg) return
    if (reg.waiting && navigator.serviceWorker.controller) announceWaiting()
    reg.addEventListener('updatefound', () => {
      const nw = reg.installing
      if (!nw) return
      nw.addEventListener('statechange', () => {
        if (nw.state === 'installed' && navigator.serviceWorker.controller) announceWaiting()
      })
    })
  }).catch(() => {})
}

// ── Stale lazy-chunk auto-recovery ────────────────────────────────────────────
// After a deploy, an installed PWA can hold old HTML whose lazy route imports
// (e.g. tapping a show → ShowDetail chunk) point at hashed files that no longer
// exist — the import throws and the route error-boundaries with no useful info.
// Vite fires `vite:preloadError` for exactly this; reload once (guarded by the
// shared claim) so the client picks up the fresh manifest instead of erroring.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()        // suppress the throw; the recovery reload is the fix
  hardReloadForUpdate()         // clear caches + unregister SW + cache-bust (guarded)
})

// Belt-and-suspenders: a stale lazy import() that rejects OUTSIDE a React boundary
// (or before one mounts) surfaces as an unhandled rejection with the MIME message —
// recover the same way instead of leaving a broken shell.
window.addEventListener('unhandledrejection', (event) => { recoverFromStaleChunk(event?.reason) })

// A synchronous error thrown outside React's render path (a raw DOM event handler,
// a setTimeout callback) hits neither the ErrorBoundary nor 'unhandledrejection' —
// it would otherwise vanish with no report and, if it happens to be a stale-chunk
// failure, no recovery either (2026-08-02 crash-resilience audit).
window.addEventListener('error', (event) => {
  if (recoverFromStaleChunk(event?.error)) return
  if (event?.error) reportError(event.error, { source: 'window.onerror' })
})

// ── Version-mismatch hard reload (last resort) ────────────────────────────────
// On every cold boot, compare the precached HTML's build ID against the live
// version.json. If they differ by more than a few minutes (i.e. the SW served
// stale HTML), force a network-bypass reload. This catches the case where the
// SW's NetworkFirst falls back to cache on a slow network and serves stale.
// EMERGENCY CIRCUIT BREAKER (2026-09-10) — Jason reported the live app reloading
// itself repeatedly (screenshot: a page torn down mid-render), losing in-progress
// actions. The 12s reloadClaimed() cooldown only spaces reloads apart — it does
// NOT stop this branch from firing again and again if `mismatch` never resolves
// to false for some reason not yet root-caused (a wedged SW, an edge-cache
// anomaly — under investigation). Until that's confirmed fixed, hard-cap how many
// auto-reloads this tab will EVER fire per version target: if we've already tried
// and it didn't resolve the mismatch, stop reloading and just log it instead of
// yanking the page out from under someone a third time.
const LOOP_KEY = 'nextep_vercheck_loop'
const MAX_AUTO_RELOADS = 1
function loopBreakerTripped(targetVersion) {
  try {
    const raw = sessionStorage.getItem(LOOP_KEY)
    const rec = raw ? JSON.parse(raw) : null
    const count = (rec && rec.v === targetVersion) ? rec.n : 0
    if (count >= MAX_AUTO_RELOADS) return true
    sessionStorage.setItem(LOOP_KEY, JSON.stringify({ v: targetVersion, n: count + 1 }))
    return false
  } catch { return false }
}

const runVersionCheck = async () => {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return
    const v = await res.json()
    if (!v?.version) return
    const mismatch = APP_BUILD_ID !== 'dev' && APP_BUILD_ID !== v.version
    // Always sync so a single reload resolves the mismatch on next boot (prevents loops)
    localStorage.setItem('nextep_boot_version', v.version)
    // Booted on the live version → the pending-update toast has done its job; clear
    // the once-per-version key so the NEXT deploy can announce itself.
    if (!mismatch) { clearQueuedUpdateAnnouncement(); try { sessionStorage.removeItem(LOOP_KEY) } catch {} }
    if (mismatch) {
      // Circuit breaker gates only the NON-standalone branch below (see its own
      // comment) — 2026-09-14, found live on Jason's account. It used to gate BOTH
      // branches, but the standalone defer path is already loop-safe on its own
      // (staleChunk's `deferPending` flag), so tripping it there added no safety —
      // it only meant: one missed backgrounding within the 90s fallback window and
      // the update could never be retried again this session, silently, forever.
      // Standalone PWA: never nuke + reload a VISIBLE app on a routine version mismatch —
      // that's the touch-desync bug. Previously this just returned and waited for a
      // force-quit that most people never do (2026-08-22: Jason asked for a way to stop
      // asking for that). deferReloadToBackground clears caches + reloads silently the
      // next time the app goes to background, so it applies with nothing for the user
      // to do, no blocking splash while it waits. Genuine breakage still self-heals via
      // the stale-chunk handlers + the white-screen watchdog below (those stay immediate).
      if (isStandalonePWA) {
        if (reloadClaimed()) return
        // reloadClaimed() yields to a real reload already in flight; this branch must
        // NOT claim the window itself — it schedules no navigation, and a claim here
        // blocked hardReloadForUpdate (a genuine stale chunk) for 12s with nothing
        // coming to replace the page. runVersionCheck re-runs on
        // every cold boot and then again on the next foreground, and will keep
        // re-detecting this exact mismatch until the deferred reload actually
        // happens — more than 12s apart, this branch runs again and used to fire
        // a second identical toast. The toast itself is now deduped inside
        // deferReloadToBackground (the `deferPending` flip), not by this cooldown.
        // This path fires from a background version check, with no user tap to
        // acknowledge — previously totally silent on standalone (Jason 2026-08-22:
        // "I got both... none on the installed app"). Same toast WhatsNew's own
        // "Update now" button shows, so every update path ends in the same visible
        // signal instead of some being silent and some not.
        deferReloadToBackground()
        // Deduped by TARGET version in localStorage (not just in-memory) so a page
        // load before the deferred swap — or WhatsNew's own detector — can't fire
        // it a second time (Jason 2026-09-05, "one and then a second on a new page load").
        announceQueuedUpdate(v.version)
        return
      }
      // Browser tab: this branch navigates immediately and unconditionally, so it's
      // the one that genuinely CAN loop if the mismatch never resolves (a wedged SW,
      // a CDN edge lagging) — the breaker belongs here, not on the self-guarding
      // standalone path above.
      if (loopBreakerTripped(v.version)) {
        // Not a crash — an expected guard. Logged as its own event so crash telemetry stays clean (2026-10-12).
        try { logEvent('update_loop_breaker', { targetVersion: v.version }) } catch {}
        return
      }
      if (reloadClaimed()) return   // a peer reload (controllerchange/preloadError) is handling it, don't double up
      claimReload()
      // A version mismatch = the SW served a STALE shell. Clear caches AND unregister the SW.
      // Clearing caches alone left the SW registered and able to re-serve stale HTML on the
      // very next load, so the reload never actually healed it — the "login rendered but
      // broken, had to delete + re-add the PWA" bug (the blank-screen watchdog can't catch it
      // because #root isn't empty). Unregister forces a clean network fetch; registerSW.js
      // re-registers a fresh SW on the next load. If a mismatch somehow persists (rare CDN
      // lag), the 12s cooldown expires and the next cold-boot/foreground check retries —
      // bounded and self-healing, no loop.
      try { if ('caches' in window) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))) } } catch {}
      try { const reg = await navigator.serviceWorker?.getRegistration(); await reg?.unregister() } catch {}
      localStorage.setItem('nextep_boot_version', v.version)
      // Use replace + cache-bust so a (re-registering) SW can't serve stale. PRESERVE existing
      // query params (esp. ?from=tvtime / ?invite=) so a stale-cache visitor arriving on a
      // campaign link keeps their framing/attribution instead of landing generic.
      try {
        const u = new URL(window.location.href)
        u.searchParams.set('_v', v.version)
        window.location.replace(u.pathname + u.search + u.hash)
      } catch { window.location.replace(`/?_v=${v.version}`) }
      return
    }
  } catch {}
}
// Defer to idle: this is a stale-precache RECOVERY check, not first-paint-critical.
// Running it at t=0 competed with the auth/session request that actually gates the UI;
// at idle it no longer steals the cold-start network slot. (cache:'no-store' already
// bypasses the HTTP cache, so the old ?cb= buster was redundant — dropped.)
;(window.requestIdleCallback || ((cb) => setTimeout(cb, 800)))(runVersionCheck)

// Re-check on FOREGROUND too. An installed PWA (especially on iOS) stays "warm" for
// days, so the cold-boot check alone leaves a long-lived session stuck on the old
// build after a deploy — the "app looks stale until I fully close + reopen it" bug.
// When the app becomes visible again we re-run the same guarded check; it only
// reloads on an actual build mismatch, and the shared cooldown prevents any double
// reload. Throttled so rapid tab switches don't re-run it needlessly.
let lastForegroundCheck = 0
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return
  const now = Date.now()
  if (now - lastForegroundCheck < 60000) return
  lastForegroundCheck = now
  runVersionCheck()
  // Also nudge the browser to look for a new service worker. iOS PWAs check on their own
  // only rarely, so without this a waiting worker (and thus the "Update now" prompt) can
  // lag a deploy by a long time. update() is cheap and the browser dedupes it.
  try { navigator.serviceWorker?.getRegistration().then(reg => reg?.update()).catch(() => {}) } catch {}
})

// ── Foreground update polling ─────────────────────────────────────────────────
// The check above only runs on hidden → visible. An app left OPEN in the
// foreground therefore never looks for a new worker, so a deploy can land and the
// "Update now" prompt simply never appears until you background the app or force
// close it — which is exactly what Jason hit (2026-07-29): sitting on the app
// waiting did nothing, force close + reopen produced it immediately.
//
// The SW lifecycle wiring was right; nothing was asking the browser to LOOK.
// reg.update() is a conditional request the browser dedupes, so a poll while
// visible is cheap. Paused while hidden — a backgrounded app doesn't need it, and
// visibilitychange already covers the return.
const FOREGROUND_UPDATE_MS = 3 * 60 * 1000
let fgUpdateTimer = null
const pollForUpdate = () => {
  try { navigator.serviceWorker?.getRegistration().then(reg => reg?.update()).catch(() => {}) } catch {}
}
const startForegroundPolling = () => {
  if (fgUpdateTimer || !('serviceWorker' in navigator)) return
  fgUpdateTimer = setInterval(() => { if (!document.hidden) pollForUpdate() }, FOREGROUND_UPDATE_MS)
}
const stopForegroundPolling = () => { if (fgUpdateTimer) { clearInterval(fgUpdateTimer); fgUpdateTimer = null } }
document.addEventListener('visibilitychange', () => {
  document.hidden ? stopForegroundPolling() : startForegroundPolling()
})
startForegroundPolling()

// If this boot is the result of an update reload, re-show the "Updating…" splash
// immediately (before React mounts) so the old cached layout can't flash through
// while the fresh bundle boots. Cleared post-paint below (Jason 2026-07-22).
if (isUpdatingReload()) showUpdatingSplash()

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary label="the app" fallback={AppCrashFallback}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </ErrorBoundary>
)

// Fade out the index.html boot splash once React has actually committed + painted
// (two rAFs ⇒ past the first paint), then remove it after the CSS fade. Guarded so
// a missing node or double-call can't throw. The white-screen watchdog keys off
// #root (not the splash), so removing this never masks a failed mount.
;(function dismissBootSplash() {
  const splash = document.getElementById('boot-splash')
  if (!splash) return
  // rAF is PAUSED in a backgrounded tab (the comment on the update splash below says
  // exactly this) — and this dismiss had ONLY the rAF path, so an app that booted in the
  // background (iOS PWA resume, tab restore, a hidden browser pane) kept the splash at
  // opacity 1 with pointer-events on: a fully rendered app under a cover that ate every
  // tap. Seen live 2026-10-12. Same belt-and-braces as the update splash: rAF when
  // visible, a timer regardless, and the next foreground, whichever comes first.
  let done = false
  const hide = () => {
    if (done) return
    done = true
    splash.classList.add('hide')
    setTimeout(() => { try { splash.remove() } catch {} }, 450)
    document.removeEventListener('visibilitychange', onVisible)
  }
  const onVisible = () => { if (!document.hidden) hide() }
  requestAnimationFrame(() => requestAnimationFrame(hide))
  setTimeout(hide, 2500)
  document.addEventListener('visibilitychange', onVisible)
})()

// Clear the update splash once the fresh app has painted. rAF is the fast path when the
// tab is visible — but rAF is PAUSED in a backgrounded tab, which would leave the cover
// stuck forever if the app boots in the background (iOS PWA resume, tab restore). So also
// clear on a setTimeout (fires when backgrounded, just throttled) and on the next
// foreground, whichever comes first. clearUpdatingSplash is idempotent (Jason 2026-07-22).
if (isUpdatingReload()) {
  requestAnimationFrame(() => requestAnimationFrame(() => clearUpdatingSplash()))
  setTimeout(() => clearUpdatingSplash(), 2000)
  const onVisible = () => { if (!document.hidden) { clearUpdatingSplash(); document.removeEventListener('visibilitychange', onVisible) } }
  document.addEventListener('visibilitychange', onVisible)
}

// ── iOS standalone: re-anchor position:fixed after a programmatic (update) navigation ──
// On an iOS home-screen PWA, a JS-driven reload/replace can leave the layout viewport that
// position:fixed anchors to transiently mis-sized — so the bottom nav AND the voice FAB
// (both position:fixed, portaled to <body>, no transformed ancestor) float up to mid-screen
// until the user scrolls. Jason hit this right after an update (2026-07-22). We can't fully
// prevent the iOS miscompute, but any reflow re-anchors the fixed layer, so we force one:
// on every pageshow, a few beats after an update boot (iOS settles the viewport late), and
// on the user's FIRST touch. NOTE: with the standalone auto-reload suppression above, our
// own update paths no longer reload here, so this mainly covers a stale-chunk recovery
// reload (rare). Kept as harmless belt-and-suspenders; isStandalonePWA is defined up top.
if (isStandalonePWA) {
  const reanchorFixed = () => {
    try {
      window.dispatchEvent(new Event('resize'))     // the signal iOS uses to recompute the ICB
      const y = window.scrollY || 0
      window.scrollTo(0, y + 1)                      // a 1px jog forces a fixed-layer relayout
      requestAnimationFrame(() => window.scrollTo(0, y))
    } catch { /* ignore */ }
  }
  window.addEventListener('pageshow', reanchorFixed)
  if (isUpdatingReload()) {
    ;[150, 500, 1200].forEach(ms => setTimeout(reanchorFixed, ms))
    window.addEventListener('touchstart', reanchorFixed, { once: true, passive: true })
  }
}

// ── White-screen watchdog (last-ditch recovery) ───────────────────────────────
// If nothing ever mounts into #root, an UPDATE failed hard — a stale precache, a
// lazy chunk that no longer exists, or a bad SW state — and the other reloaders
// (controllerchange / preloadError / version-mismatch) can be suppressed by the
// shared 12s cooldown, leaving the app stuck BLANK. This is an independent, one-
// shot net: if the root is still empty after a few seconds, clear all caches,
// unregister the SW, and hard-reload from the network. It resets on a successful
// mount, and refuses to loop — if it's still blank after its one try, that's a
// genuine crash (not a cache issue) and reloading again wouldn't help.
const WD_KEY = 'nextep_wd_reloaded'
setTimeout(async () => {
  const root = document.getElementById('root')
  if (root && root.childElementCount > 0) { try { sessionStorage.removeItem(WD_KEY) } catch {}; return }
  try { if (sessionStorage.getItem(WD_KEY)) return; sessionStorage.setItem(WD_KEY, '1') } catch {}
  reportError(new Error('white-screen watchdog fired: #root still empty 5s after boot'))  // prod signal (no-op without DSN)
  try { if ('caches' in window) { const ks = await caches.keys(); await Promise.all(ks.map(k => caches.delete(k))) } } catch {}
  try { const reg = await navigator.serviceWorker?.getRegistration(); await reg?.unregister() } catch {}
  window.location.replace(`/?_wd=${Date.now()}`)
}, 5000)
